/**
 * Scope hierarchy (phase 1): mutation implies read at enforcement edges, not in the JWT.
 *
 * Same-resource only:
 *   create | update | delete  ⇒  satisfies *.read
 * Cross-resource never implies.
 * Mutation checks stay exact (create/update/delete require that action).
 *
 * @see sprint-6/RBAC/SCOPE_HIERARCHY.md
 */

const MUTATION_ACTIONS = new Set(["create", "update", "delete"]);

/**
 * @param {string} shortName e.g. tickets.read, settings.sap-landscape.update
 * @returns {{ resource: string, action: string } | null}
 */
export function splitScopeName(shortName) {
  if (!shortName || typeof shortName !== "string") return null;
  const i = shortName.lastIndexOf(".");
  if (i <= 0 || i === shortName.length - 1) return null;
  return {
    resource: shortName.slice(0, i),
    action: shortName.slice(i + 1),
  };
}

/**
 * Scopes that satisfy a required short name (exact for mutations; OR set for *.read).
 * @param {string} requiredShortName
 * @param {Iterable<string>} catalogScopeNames
 * @returns {string[]}
 */
export function scopesThatSatisfy(requiredShortName, catalogScopeNames) {
  const parts = splitScopeName(requiredShortName);
  if (!parts) return requiredShortName ? [requiredShortName] : [];

  const catalog = catalogScopeNames instanceof Set
    ? catalogScopeNames
    : new Set(catalogScopeNames);

  if (parts.action !== "read") {
    return catalog.has(requiredShortName) ? [requiredShortName] : [requiredShortName];
  }

  const out = [];
  for (const name of catalog) {
    const p = splitScopeName(name);
    if (!p || p.resource !== parts.resource) continue;
    if (p.action === "read" || MUTATION_ACTIONS.has(p.action)) {
      out.push(name);
    }
  }
  if (out.length === 0 && catalog.has(requiredShortName)) {
    return [requiredShortName];
  }
  return out.sort();
}

/**
 * True if scopeSet satisfies requiredShortName under hierarchy rules.
 * @param {Set<string>} scopeSet
 * @param {string} requiredShortName
 * @param {Iterable<string>} catalogScopeNames
 */
export function hasScopeOrImplied(scopeSet, requiredShortName, catalogScopeNames) {
  if (!requiredShortName || !scopeSet) return false;
  const acceptable = scopesThatSatisfy(requiredShortName, catalogScopeNames);
  return acceptable.some((name) => scopeSet.has(name));
}

/**
 * For Group Management: which *.read scopes are implied by selected mutations
 * (not necessarily stored in the pack).
 * @param {string[]} selectedScopes
 * @param {Iterable<string>} catalogScopeNames
 * @returns {Set<string>} read scope names that are implied
 */
export function impliedReadScopes(selectedScopes, catalogScopeNames) {
  const catalog = catalogScopeNames instanceof Set
    ? catalogScopeNames
    : new Set(catalogScopeNames);
  const implied = new Set();
  for (const name of selectedScopes || []) {
    const p = splitScopeName(name);
    if (!p || !MUTATION_ACTIONS.has(p.action)) continue;
    const readName = `${p.resource}.read`;
    if (catalog.has(readName)) implied.add(readName);
  }
  return implied;
}
